import { ENUM_LABELS, type RequisitionDetail } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { useCan } from '#client/hooks/useCan';
import { RESOURCE } from '#client/lib/routes';
import { isRequisitionDraft, transitionsFrom } from './requisition-actions';
import { canReceive } from './requisition.logic';
import {
  Deliveries,
  ReagentsCard,
  RejectForm,
  RequisitionActions,
  RequisitionDetails,
} from './RequisitionDetailSections';
import { useRequisitionMove } from './useRequisitionMove';

export function RequisitionDetailPage() {
  return (
    <ResourceDetailPage<RequisitionDetail>
      path={RESOURCE.requisitions}
      noun="reagent order"
      title={(row) => row.number}
      subtitle={(row) => ENUM_LABELS.requisitionStatus[row.status]}
      deleteLabel={(row) => row.number}
      editCapability="requisition.edit"
      deleteCapability="requisition.delete"
      canEdit={(row) => isRequisitionDraft(row.status)}
      canDelete={(row) => isRequisitionDraft(row.status)}
      fullWidth
    >
      {(row, refresh) => <Body requisition={row} onChanged={refresh} />}
    </ResourceDetailPage>
  );
}

interface BodyProps {
  requisition: RequisitionDetail;
  /** Reloads the order after a transition. */
  onChanged: () => void;
}

function Body({ requisition, onChanged }: BodyProps) {
  const can = useCan();
  const moveRequest = useRequisitionMove(requisition, onChanged);

  return (
    <div className="c-form-stack">
      {moveRequest.errorMessage && <Banner tone="critical">{moveRequest.errorMessage}</Banner>}

      {requisition.status === 'rejected' && requisition.rejection_reason && (
        <Banner tone="critical" title="Rejected">
          {requisition.rejection_reason}
        </Banner>
      )}

      <Card
        title="This order"
        actions={
          <RequisitionActions
            requisitionId={requisition.id}
            actions={transitionsFrom(requisition.status, can)}
            pendingTo={moveRequest.pendingTo}
            activeTo={moveRequest.activeTo}
            canRecordDelivery={can('requisition.receive') && canReceive(requisition.status)}
            onMove={(to) => void moveRequest.move(to)}
          />
        }
      >
        {moveRequest.isRejecting && (
          <RejectForm
            reason={moveRequest.reason}
            onReasonChange={moveRequest.setReason}
            onReject={() => void moveRequest.move('rejected')}
          />
        )}

        <RequisitionDetails requisition={requisition} />
      </Card>

      <ReagentsCard lines={requisition.lines} />

      <Deliveries receipts={requisition.receipts} />
    </div>
  );
}
